import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowUpRight,
  Building2,
  ChevronDown,
  Copy,
  CreditCard,
  Database,
  Eye,
  FileText,
  GripVertical,
  Landmark,
  MapPin,
  PenLine,
  Plus,
  ScanFace,
  Smartphone,
  Timer,
  Trash,
  Trash2,
  TrendingUp,
  Upload,
  X,
} from 'lucide-react';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { QuestionnaireStepPanel } from '@/features/questionnaires/components/QuestionnaireStepPanel';
import { EsignStepPanel } from '@/features/templates/components/EsignStepPanel';
import { TemplateStepPanel } from '@/features/templates/components/TemplateStepPanel';
import { cn } from '@/lib/utils';
import { useApiCatalogue } from '../../api/useApiCatalogue';
import type {
  StepConditionDto,
  StepReturnFieldDto,
  StepRiskRuleDto,
  WorkflowStepDto,
} from '../../schemas/workflowSchemas';
import { STEP_TYPE_CATALOGUE_TAGS, STEP_TYPE_META } from '../../types';
import { EmailVerificationStepPanel } from './EmailVerificationStepPanel';
import { hasPreviewScreen } from './MobilePreview';

// ── Step icon map ────────────────────────────────────────────────────────────

const STEP_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Building2,
  CreditCard,
  Database,
  Eye,
  FileText,
  Landmark,
  MapPin,
  PenLine,
  ScanFace,
  Smartphone,
  TrendingUp,
  Upload,
};

// ── Settings data shape + helpers ────────────────────────────────────────────

type StepSettingsData = {
  maxVerification: number;
  lockDuration: number;
  autoLockOnExceed: boolean;
  notifyOnFailure: boolean;
};

// Exported so the editor can SEED these onto a newly added step (and backfill
// steps that were saved with null settings) — otherwise the panel only shows
// these as placeholders and nothing is persisted until a control is touched.
export const SETTINGS_DEFAULTS: StepSettingsData = {
  maxVerification: 3,
  lockDuration: 24,
  autoLockOnExceed: true,
  notifyOnFailure: true,
};

// The default settings as the JSON string stored on WorkflowStepDto.settings.
export const DEFAULT_STEP_SETTINGS_JSON = JSON.stringify(SETTINGS_DEFAULTS);

function parseSettings(raw: string | null): StepSettingsData {
  try {
    return raw ? { ...SETTINGS_DEFAULTS, ...JSON.parse(raw) } : SETTINGS_DEFAULTS;
  } catch {
    return SETTINGS_DEFAULTS;
  }
}

// Raw settings object — preserves unknown keys (e.g. `__ruleGroups`) that the
// typed StepSettingsData shape doesn't model. Used to read/write the Advanced
// Rules grouping without clobbering the SettingsTab values stored in the same
// JSON blob.
function parseSettingsObj(raw: string | null): Record<string, unknown> {
  try {
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

// ── Risk rule metadata ───────────────────────────────────────────────────────

const RISK_RULE_META: Record<string, { label: string; description: string }> = {
  pan_status: {
    label: 'PAN Status',
    description:
      'Applies only when the PAN is inactive or invalid. A valid, active PAN always passes — this action is what to do with an invalid/inactive PAN.',
  },
  pan_aadhaar_link: {
    label: 'PAN-Aadhaar Linking Status',
    description:
      'Applies only when the PAN and Aadhaar are NOT linked. A linked PAN always passes — this action is what to do when they are not linked.',
  },
  pan_exists: {
    label: 'PAN Already Exists in System',
    description:
      'Applies when this PAN was already verified in an earlier session of this workflow.',
  },
  age_restriction: {
    label: 'Age Restriction',
    description: 'Your age does not meet the eligibility criteria for this service.',
  },
  name_match_score: {
    label: 'Name Match Score',
    description: 'Indicates how closely your name matches the records available in the system.',
  },
  face_match_fail: {
    label: 'Face Match Fail',
    description:
      'Applies when the face match score against the reference photo is below the threshold — the selfie may not belong to the applicant.',
  },
  liveness_score: {
    label: 'Liveness Fail',
    description:
      'Applies when the liveness score is below the threshold — the capture may not be a live person.',
  },
  multiple_faces: {
    label: 'Multiple Faces Detected',
    description: 'More than one face was detected in the capture frame.',
  },
  review_needed: {
    label: 'Review Needed',
    description: 'The verification API flagged the result for manual review.',
  },
  account_inactive: {
    label: 'Account Inactive',
    description:
      'Applies when the bank reports the account as inactive / not verified. An active account always passes — this action is what to do when it is inactive.',
  },
  name_mismatch: {
    label: 'Name Mismatch',
    description:
      'Applies when the bank account holder name does not match the applicant’s verified PAN / Aadhaar name above the threshold.',
  },
  duplicate_account: {
    label: 'Duplicate Account',
    description:
      'Applies when this bank account (account number + IFSC) was already verified in an earlier session of this workflow.',
  },
  gstin_status_not_active: {
    label: 'GSTIN Status Not Active',
    description:
      'Applies when the GSTIN status is not Active (e.g. Cancelled or Suspended). An Active GSTIN always passes — this action is what to do otherwise.',
  },
  business_vintage_restriction: {
    label: 'Business Vintage Restriction',
    description:
      'Applies when the business was registered more recently than the configured minimum number of months ago.',
  },
  legal_name_match: {
    label: 'Legal Name Match Score',
    description:
      'Applies when the GST legal name of business does not match the applicant’s verified PAN name above the threshold.',
  },
  gstin_already_exists: {
    label: 'GSTIN Already Exists in System',
    description:
      'Applies when this GSTIN was already verified in an earlier session of this workflow.',
  },
  duplicate_uid_detected: {
    label: 'Duplicate UID Detected',
    description:
      'Applies when the ration card record flags a duplicate UID (Aadhaar) among its members.',
  },
  missing_ration_address: {
    label: 'Missing Address',
    description: 'Applies when the ration card carries no address — a weaker proof of address.',
  },
  duplicate_ration_card: {
    label: 'Ration Card Already Exists in System',
    description:
      'Applies when this ration card was already verified in an earlier session of this workflow.',
  },
  // ── MSME (Udyam) ──
  enterprise_type_restriction: {
    label: 'Enterprise Type Restriction',
    description:
      'Applies when the enterprise size (Micro / Small / Medium) is not in the configured allowed list.',
  },
  pan_mismatch: {
    label: 'PAN Mismatch',
    description:
      "Applies when the enterprise PAN in the Udyam record does not match the applicant's verified PAN from an earlier PAN step.",
  },
  turnover_threshold_breach: {
    label: 'Turnover Threshold Breach',
    description: 'Applies when the net turnover exceeds the configured limit.',
  },
  recent_incorporation: {
    label: 'Recent Incorporation',
    description:
      'Applies when the enterprise was incorporated more recently than the configured minimum vintage.',
  },
  duplicate_udyam: {
    label: 'Udyam Registration Already Exists in System',
    description:
      'Applies when this Udyam registration number was already verified in an earlier session of this workflow.',
  },
};

// Rules that carry a numeric threshold the admin can set. The value is stored on
// the rule's `config` (a plain number string) and read by the backend verifier
// (ReadThreshold): age_restriction fires when age < value; name_match_score fires
// when the match score < value.
const RULE_CONFIG_META: Record<
  string,
  { label: string; placeholder: number; min: number; max: number; suffix: string }
> = {
  age_restriction: { label: 'Minimum age', placeholder: 18, min: 1, max: 120, suffix: 'years' },
  name_match_score: {
    label: 'Minimum match score',
    placeholder: 70,
    min: 0,
    max: 100,
    suffix: '%',
  },
  // Spec §6.2: face match threshold 80, range 60–99.
  face_match_fail: {
    label: 'Minimum face match score',
    placeholder: 80,
    min: 60,
    max: 99,
    suffix: '%',
  },
  // Spec §7.2: liveness score 85, range 50–99.
  liveness_score: {
    label: 'Minimum liveness score',
    placeholder: 85,
    min: 50,
    max: 99,
    suffix: '%',
  },
  // Spec §8.3: name-match threshold, default 75.
  name_mismatch: {
    label: 'Minimum name match score',
    placeholder: 75,
    min: 0,
    max: 100,
    suffix: '%',
  },
  // GST spec: minimum months since registration, default 6.
  business_vintage_restriction: {
    label: 'Minimum business vintage',
    placeholder: 6,
    min: 0,
    max: 240,
    suffix: 'months',
  },
  // GST spec: legal-name-vs-PAN match threshold, default 70.
  legal_name_match: {
    label: 'Minimum name match score',
    placeholder: 70,
    min: 0,
    max: 100,
    suffix: '%',
  },
  // ID Verification — Voter ID staleness (months) + Passport name-score threshold.
  stale_electoral_record: {
    label: 'Max record age',
    placeholder: 36,
    min: 1,
    max: 240,
    suffix: 'months',
  },
  name_score_low: {
    label: 'Minimum name match score',
    placeholder: 70,
    min: 0,
    max: 100,
    suffix: '%',
  },
  // Utility Bill — bill / booking staleness thresholds (months), per the PDF's
  // Bill Recency (default 3) and Booking Recency (default 6) configuration.
  bill_recency_restriction: {
    label: 'Max bill age',
    placeholder: 3,
    min: 1,
    max: 240,
    suffix: 'months',
  },
  booking_recency_restriction: {
    label: 'Max booking age',
    placeholder: 6,
    min: 1,
    max: 240,
    suffix: 'months',
  },
  // MSME (Udyam) — net-turnover limit (₹) + minimum incorporation vintage (months).
  turnover_threshold_breach: {
    label: 'Net turnover limit',
    placeholder: 500000000,
    min: 0,
    max: 100000000000,
    suffix: '₹',
  },
  recent_incorporation: {
    label: 'Minimum vintage',
    placeholder: 6,
    min: 0,
    max: 600,
    suffix: 'months',
  },
};

// ── Per-sub-type helpers (ID Verification + Utility Bill) ────────────────────
// The combined ID Verification and Utility Bill steps namespace every risk-rule /
// return-field / condition key as "<subType>::<key>" so their sub-types (ID: Voter
// ID / Passport / Driving Licence; Utility: Electricity / PNG / LPG) can be
// configured independently in one step. Every OTHER step is un-prefixed, so
// splitDocScope returns doc=null and the tabs render exactly as before. The
// RiskRules / Return-Data tabs skip any sub-type with no rows, so listing both
// steps' sub-types here is safe — only the ones actually present render.
const ID_DOC_LABELS: Record<string, string> = {
  voter_id: 'Voter ID',
  passport: 'Passport',
  driving_license: 'Driving Licence',
  electricity: 'Electricity',
  png: 'PNG Connection',
  lpg: 'LPG',
};
const ID_DOC_ORDER = ['voter_id', 'passport', 'driving_license', 'electricity', 'png', 'lpg'];

function splitDocScope(key: string): { doc: string | null; key: string } {
  const i = key.indexOf('::');
  return i < 0 ? { doc: null, key } : { doc: key.slice(0, i), key: key.slice(i + 2) };
}

// ID Verification risk-rule metadata (looked up by the CLEAN key after the
// "<doc>::" prefix is stripped). Merged over RISK_RULE_META at lookup time.
const ID_RISK_RULE_META: Record<string, { label: string; description: string }> = {
  stale_electoral_record: {
    label: 'Stale Electoral Record',
    description:
      'Applies when the electoral record was last updated longer ago than the configured number of months.',
  },
  epic_already_exists: {
    label: 'EPIC Already Exists in System',
    description:
      'Applies when this Voter ID (EPIC) was already verified in an earlier session of this workflow.',
  },
  dl_status_not_active: {
    label: 'DL Status Not Active',
    description:
      'Applies when the licence status is not Active (e.g. Suspended / Cancelled / Expired).',
  },
  enforcement_active: {
    label: 'Enforcement / Suspension Active',
    description:
      'Applies when an enforcement or suspension order is active (an enforcement start date exists and it has not ended).',
  },
  commercial_psv: {
    label: 'Commercial DL (PSV Badge)',
    description:
      'Applies when the PSV badge is not Active — relevant only when the workflow requires driving public passenger carriers.',
  },
  dl_already_exists: {
    label: 'DL Already Exists in System',
    description:
      'Applies when this Driving Licence was already verified in an earlier session of this workflow.',
  },
  passport_already_exists: {
    label: 'Passport Already Exists in System',
    description:
      'Applies when this passport number was already verified in an earlier session of this workflow.',
  },
  passport_number_mismatch: {
    label: 'Passport Number Mismatch',
    description: 'Applies when the passport number does not match the source record.',
  },
  date_of_issue_mismatch: {
    label: 'Date of Issue Mismatch',
    description: 'Applies when the date of issue does not match the source record.',
  },
  name_score_low: {
    label: 'Name Match Score Below Threshold',
    description:
      'Applies when the name-match score against the passport record is below the configured threshold.',
  },
  // ── Utility Bill (Electricity / PNG / LPG) ──
  bill_recency_restriction: {
    label: 'Bill Recency Restriction',
    description:
      'Applies when the bill is older than the configured maximum age — the standard proof-of-address staleness guard.',
  },
  outstanding_bill_overdue: {
    label: 'Outstanding Bill Overdue',
    description:
      'Applies when the bill due date has passed and there is a positive amount payable.',
  },
  missing_address: {
    label: 'Missing Address',
    description:
      'Applies when the connection record returns no address (unusable as proof of address).',
  },
  bill_due_date_elapsed: {
    label: 'Bill Due Date Elapsed',
    description: 'Applies when the bill due date has already passed.',
  },
  consumer_status_not_active: {
    label: 'Consumer Status Not Active',
    description: 'Applies when the LPG connection status is not Active.',
  },
  booking_recency_restriction: {
    label: 'Booking Recency Restriction',
    description:
      'Applies when the last refill booking is older than the configured maximum age — flags dormant connections.',
  },
  aadhaar_not_linked: {
    label: 'Aadhaar Not Linked',
    description: 'Applies when no Aadhaar number is linked to the LPG connection.',
  },
  duplicate_consumer_number: {
    label: 'Duplicate Consumer Number',
    description:
      'Applies when this connection was already verified in an earlier session of this workflow.',
  },
  duplicate_bill_number: {
    label: 'Duplicate Bill / Connection',
    description:
      'Applies when this PNG connection was already verified in an earlier session of this workflow.',
  },
};

// ── Public interface ─────────────────────────────────────────────────────────

export interface StepSettingsPanelProps {
  step: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
  onClose: () => void;
  onDelete: () => void;
  onSave: () => void;
  isSaving: boolean;
  isDirty: boolean;
  /** Hide the Save button inside the panel — use when the host provides its own save action. */
  showSave?: boolean;
  /** Read-only (View mode): tab switching still works, but every editing control is disabled. */
  readOnly?: boolean;
  /**
   * Designer behaviour: enabling an API toggle in the Method tab jumps the
   * phone preview to that API's screen (row index 0/1/2).
   */
  onPreviewApiToggled?: (index: number) => void;
  /**
   * Return-Data fields that must be DISABLED (not toggle-able) because they
   * depend on data an earlier step has to provide. Map of fieldKey → the reason
   * shown under the toggle (e.g. Aadhaar "Name Match Score vs PAN" needs an
   * earlier PAN step that returns PAN Holder Name). Computed by the host, which
   * is the only place that can see the other steps.
   */
  returnFieldGuards?: Record<string, string>;
  /**
   * Questionnaire ids already attached to the workflow's OTHER steps, so a Questionnaire step cannot
   * be pointed at one another step already asks. Computed by the host — the only place that can see
   * the sibling steps, same as `returnFieldGuards` above.
   */
  takenQuestionnaireIds?: readonly string[];
}

type PanelTab = 'method' | 'settings' | 'risk' | 'advanced' | 'return';

const TABS: { id: PanelTab; label: string }[] = [
  { id: 'method', label: 'Method' },
  { id: 'settings', label: 'Settings' },
  { id: 'risk', label: 'Risk Rules' },
  { id: 'advanced', label: 'Advanced Rules' },
  { id: 'return', label: 'Return Data' },
];

// ── Main panel component ─────────────────────────────────────────────────────

export function StepSettingsPanel({
  step,
  onUpdate,
  onClose,
  onDelete,
  onSave,
  isSaving,
  isDirty,
  showSave = true,
  readOnly = false,
  onPreviewApiToggled,
  returnFieldGuards,
  takenQuestionnaireIds,
}: StepSettingsPanelProps) {
  const [activeTab, setActiveTab] = useState<PanelTab>('method');

  // Reset tab when the step changes
  const prevStepId = useRef(step.id);
  useEffect(() => {
    if (prevStepId.current !== step.id) {
      setActiveTab('method');
      prevStepId.current = step.id;
    }
  }, [step.id]);

  const stepMeta = STEP_TYPE_META[step.stepType];
  const StepIconComp =
    (stepMeta?.icon != null ? STEP_ICON_MAP[stepMeta.icon] : undefined) ?? FileText;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-grayEF shadow-[0px_2px_12px_0px_rgba(0,0,0,0.08)]">
      {/* Panel header — Figma Frame 154: h-[36px] bg-[#f4f6fd] px-4 */}
      <div className="flex h-9 shrink-0 items-center justify-between bg-[#F4F6FD] px-4">
        <div className="flex items-center gap-2 min-w-0">
          <StepIconComp className="w-[18px] h-[18px] shrink-0 text-black-logo" />
          <span className="text-base font-semibold tracking-tight text-black-logo truncate">
            {/* Prefer the step-type label so the heading always reflects the
                current name (there is no rename UI; displayName is seeded from
                this label on add). Fixes older steps saved as "Aadhaar (OTP)". */}
            {stepMeta?.label ?? step.displayName}
          </span>
        </div>
        <div className="flex items-center gap-[10px] shrink-0 ms-2">
          {!readOnly && (
            <>
              <button
                type="button"
                title="Copy step"
                className="text-gray66 hover:text-primary transition-colors"
              >
                <Copy className="w-4 h-4" />
              </button>
              <button
                type="button"
                title="Delete step"
                onClick={onDelete}
                className="text-gray66 hover:text-primary transition-colors"
              >
                <Trash className="w-4 h-4" />
              </button>
            </>
          )}
          {/* Close — Figma: 18px white circle with 14px X inside */}
          <button
            type="button"
            title="Close"
            onClick={onClose}
            className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white hover:bg-grayEF transition-colors"
          >
            <X className="h-3.5 w-3.5 text-primary" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col overflow-hidden p-4">
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/*
            A Document Template step configures a document, not an API call — none of
            the five tabs below (Method / Risk / Advanced / Return Data) has anything
            to show for it, so it gets its own panel instead of five empty ones.
          */}
          {/*
            A Questionnaire step collects answers and calls no provider, so — like Document
            Template — none of the five tabs applies and it gets its own panel rather than five
            empty ones.
          */}
          {/*
            An Email Verification step sends its own code and calls no provider, so — like the two
            below — none of the five tabs applies and it gets its own panel rather than five empty
            ones.
          */}
          {step.stepType === 'email_verification' ? (
            <div className="rounded-lg border border-[#F3F3F3] p-4">
              <EmailVerificationStepPanel step={step} onUpdate={onUpdate} readOnly={readOnly} />
            </div>
          ) : step.stepType === 'questionnaire' ? (
            <div className="rounded-lg border border-[#F3F3F3] p-4">
              <QuestionnaireStepPanel
                step={step}
                onUpdate={onUpdate}
                readOnly={readOnly}
                takenQuestionnaireIds={takenQuestionnaireIds}
                // Passed for the same reason TemplateStepPanel gets them: creating a questionnaire
                // navigates to another route, which unmounts this editor and would discard every
                // unsaved edit — including the step being configured right now.
                isDirty={isDirty}
                onSave={onSave}
              />
            </div>
          ) : step.stepType === 'document_template' ? (
            <div className="rounded-lg border border-[#F3F3F3] p-4">
              <TemplateStepPanel
                step={step}
                onUpdate={onUpdate}
                readOnly={readOnly}
                isDirty={isDirty}
                onSave={onSave}
              />
            </div>
          ) : step.stepType === 'esign_template' ? (
            /* h-full, not auto: the panel fills the scroll area exactly so the page
               viewer inside owns the only scrollbar. Sized to content it would be
               taller than the area and produce a second one around it. */
            <div className="h-full rounded-lg border border-[#F3F3F3] p-4">
              <EsignStepPanel step={step} onUpdate={onUpdate} readOnly={readOnly} />
            </div>
          ) : (
            <div className="rounded-lg border border-[#F3F3F3] p-4">
              {/* Tab bar — overflow-x-auto + whitespace-nowrap prevents wrapping on narrow panels */}
              <nav className="flex items-center gap-4 border-b border-grayEF overflow-x-auto hide-scrollbar">
                {TABS.map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveTab(id)}
                    className={cn(
                      'relative shrink-0 border-b-2 pb-1.5 text-sm font-medium tracking-tight transition-colors outline-none whitespace-nowrap',
                      activeTab === id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray66 hover:text-black-logo',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </nav>

              {/*
              Tab CONTENT only. A disabled <fieldset> natively disables every
              input/select/button inside — making the fields read-only in View
              mode. It's deliberately placed AFTER the tab <nav> above so tab
              switching stays clickable while the content can't be edited.
              `contents` keeps the layout unchanged.
            */}
              <fieldset disabled={readOnly} className="contents">
                <div className={cn('pt-4', activeTab !== 'method' && 'hidden')}>
                  <MethodTab
                    draft={step}
                    onUpdate={onUpdate}
                    onPreviewApiToggled={onPreviewApiToggled}
                  />
                </div>
                <div className={cn('pt-4', activeTab !== 'settings' && 'hidden')}>
                  <SettingsTab draft={step} onUpdate={onUpdate} />
                </div>
                <div className={cn('pt-4', activeTab !== 'risk' && 'hidden')}>
                  <RiskRulesTab draft={step} onUpdate={onUpdate} />
                </div>
                <div className={cn('pt-4', activeTab !== 'advanced' && 'hidden')}>
                  <AdvancedRulesTab draft={step} onUpdate={onUpdate} />
                </div>
                <div className={cn('pt-4', activeTab !== 'return' && 'hidden')}>
                  <ReturnDataTab draft={step} onUpdate={onUpdate} guards={returnFieldGuards} />
                </div>
              </fieldset>
            </div>
          )}
        </div>

        {/* Save button — hidden when host provides its own save action */}
        {showSave && (
          <div className="pt-4 text-center">
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving || !isDirty}
              className="btn btn-primary disabled:opacity-40"
            >
              Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Method Tab ───────────────────────────────────────────────────────────────

// Catalogue operationIds hidden from the Method tab. The catalogue entry (and
// all its backend/runtime code) is kept — hiding here just removes the row.
// Remove an id from this set to re-expose that API (a one-line change).
const HIDDEN_OPERATIONS = new Set<string>(['Post_Aadhaar_Ovse']);

function MethodTab({
  draft,
  onUpdate,
  onPreviewApiToggled,
}: {
  draft: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
  onPreviewApiToggled?: (index: number) => void;
}) {
  const tag = STEP_TYPE_CATALOGUE_TAGS[draft.stepType];
  const { data: rawCatalogue, isLoading } = useApiCatalogue(tag);

  // Catalogue minus the operations we deliberately hide (e.g. Aadhaar OVSE).
  // Everything downstream (rows, auto-populate, priority order) uses this so a
  // hidden API never gets a row or an auto-enabled binding — its backend code
  // stays intact; unhide by removing it from HIDDEN_OPERATIONS.
  const catalogue = useMemo(
    () => rawCatalogue?.filter((e) => !HIDDEN_OPERATIONS.has(e.operationId)),
    [rawCatalogue],
  );

  // Binding lookup by catalogue entry — the table is driven by the CATALOGUE
  // (every available API shows a row), not by the stored bindings. This way an
  // API added to the catalogue later (e.g. PAN OCR) appears in existing
  // workflows too; a binding may or may not exist yet for a given entry.
  const bindingByEntry = useMemo(() => {
    const m = new Map<string, (typeof draft.apiBindings)[number]>();
    for (const b of draft.apiBindings) m.set(b.catalogueEntryId, b);
    return m;
  }, [draft.apiBindings]);

  const catalogueOrder = useMemo(
    () => new Map((catalogue ?? []).map((e, i) => [e.id, i])),
    [catalogue],
  );

  const hasBindings = draft.apiBindings.length > 0;

  // Migrate away hidden-operation bindings (e.g. a workflow saved while OVSE was
  // still visible). Such a binding would keep a priority slot — pushing the
  // visible APIs' priority numbering off (OCR showing "2") — and, worse, still
  // run in a live session. Drop them and renumber the survivors (enabled 1..k in
  // their current order, then disabled), so the step behaves as if the hidden
  // API never existed. Uses the RAW catalogue to resolve which bindings hidden.
  useEffect(() => {
    if (!rawCatalogue) return;
    const hiddenEntryIds = new Set(
      rawCatalogue.filter((e) => HIDDEN_OPERATIONS.has(e.operationId)).map((e) => e.id),
    );
    if (hiddenEntryIds.size === 0) return;
    if (!draft.apiBindings.some((b) => hiddenEntryIds.has(b.catalogueEntryId))) return;

    const kept = draft.apiBindings.filter((b) => !hiddenEntryIds.has(b.catalogueEntryId));
    const ordered = [
      ...kept.filter((b) => b.isEnabled).sort((a, b) => a.priority - b.priority),
      ...kept.filter((b) => !b.isEnabled).sort((a, b) => a.priority - b.priority),
    ];
    const priorityByEntry = new Map(ordered.map((b, i) => [b.catalogueEntryId, i + 1]));
    onUpdate({
      apiBindings: kept.map((b) => ({
        ...b,
        priority: priorityByEntry.get(b.catalogueEntryId) ?? 1,
      })),
    });
  }, [rawCatalogue, draft.apiBindings, onUpdate]);

  // Auto-populate: when catalogue first loads and step has no bindings yet,
  // add all (visible) catalogue entries as enabled bindings in catalogue order.
  useEffect(() => {
    if (!catalogue || catalogue.length === 0 || hasBindings) return;
    onUpdate({
      apiBindings: catalogue.map((entry, idx) => ({
        id: crypto.randomUUID(),
        catalogueEntryId: entry.id,
        isEnabled: true,
        priority: idx + 1,
        inputMappings: [],
      })),
    });
  }, [catalogue, hasBindings, onUpdate]);

  const enabledCount = draft.apiBindings.filter((b) => b.isEnabled).length;

  // Rows split for the Method table: ENABLED APIs (sorted by priority) are
  // drag-sortable to reorder the fallback chain; the rest are static below.
  const enabledRows = useMemo(
    () =>
      (catalogue ?? [])
        .map((entry) => ({ entry, binding: bindingByEntry.get(entry.id) }))
        .filter((r) => r.binding?.isEnabled)
        .sort((a, b) => (a.binding?.priority ?? 0) - (b.binding?.priority ?? 0)),
    [catalogue, bindingByEntry],
  );
  const disabledRows = useMemo(
    () =>
      (catalogue ?? [])
        .map((entry) => ({ entry, binding: bindingByEntry.get(entry.id) }))
        .filter((r) => !r.binding?.isEnabled),
    [catalogue, bindingByEntry],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Drag-reorder the enabled APIs → renumber priority 1..k (enabled), then the
  // disabled bindings trail behind. Matches setEntryEnabled's numbering so the
  // unique (step, priority) constraint is always satisfied.
  function handleReorderBindings(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = enabledRows.map((r) => r.entry.id);
    const oldIdx = ids.indexOf(active.id as string);
    const newIdx = ids.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;

    const reordered = arrayMove(ids, oldIdx, newIdx);
    const priorityByEntry = new Map<string, number>();
    reordered.forEach((id, i) => {
      priorityByEntry.set(id, i + 1);
    });
    disabledRows.forEach((r, i) => {
      priorityByEntry.set(r.entry.id, reordered.length + i + 1);
    });

    onUpdate({
      apiBindings: draft.apiBindings.map((b) => ({
        ...b,
        priority: priorityByEntry.get(b.catalogueEntryId) ?? b.priority,
      })),
    });
  }

  // Enable/disable an API by catalogue entry. Priority follows the ENABLE order
  // (first API you turn on = priority 1, next = 2, …), NOT the catalogue order.
  // A newly enabled API is appended to the end of the fallback chain. Every
  // binding still gets a unique priority (enabled 1..k, then disabled k+1..n)
  // so the DB's unique (step, priority) index is never violated.
  function setEntryEnabled(entryId: string, isEnabled: boolean) {
    if (!isEnabled && enabledCount <= 1) return; // keep at least one enabled

    // Next slot after the current last enabled API.
    const maxEnabledPriority = draft.apiBindings
      .filter((b) => b.isEnabled)
      .reduce((mx, b) => Math.max(mx, b.priority), 0);

    const existing = bindingByEntry.get(entryId);
    let next = isEnabled
      ? existing
        ? draft.apiBindings.map((b) =>
            b.catalogueEntryId === entryId
              ? { ...b, isEnabled: true, priority: maxEnabledPriority + 1 }
              : b,
          )
        : [
            ...draft.apiBindings,
            {
              id: crypto.randomUUID(),
              catalogueEntryId: entryId,
              isEnabled: true,
              priority: maxEnabledPriority + 1,
              inputMappings: [],
            },
          ]
      : draft.apiBindings.map((b) =>
          b.catalogueEntryId === entryId ? { ...b, isEnabled: false } : b,
        );

    // Compact to unique 1..N. Enabled kept in their current priority order
    // (= the order they were enabled); disabled trail behind in catalogue order.
    const ordered = [
      ...next.filter((b) => b.isEnabled).sort((a, b) => a.priority - b.priority),
      ...next
        .filter((b) => !b.isEnabled)
        .sort(
          (a, b) =>
            (catalogueOrder.get(a.catalogueEntryId) ?? 999) -
            (catalogueOrder.get(b.catalogueEntryId) ?? 999),
        ),
    ];
    const priorityByEntry = new Map(ordered.map((b, i) => [b.catalogueEntryId, i + 1]));
    next = next.map((b) => ({ ...b, priority: priorityByEntry.get(b.catalogueEntryId) ?? 1 }));

    onUpdate({ apiBindings: next });

    // Designer: turning an API on focuses the phone preview on its screen. The
    // preview list is dynamic (one screen per ENABLED API, in priority order), so
    // the focus index is this API's position among the enabled APIs that have a
    // preview screen. APIs that redirect out (Aadhaar DigiLocker) have no screen,
    // so they don't jump.
    if (isEnabled) {
      const entry = (catalogue ?? []).find((e) => e.id === entryId);
      if (entry && hasPreviewScreen(entry.operationId)) {
        const enabledScreenOps = ordered
          .filter((b) => b.isEnabled)
          .map((b) => (catalogue ?? []).find((e) => e.id === b.catalogueEntryId)?.operationId)
          .filter((op): op is string => op !== undefined && hasPreviewScreen(op));
        const screenIdx = enabledScreenOps.indexOf(entry.operationId);
        if (screenIdx >= 0) onPreviewApiToggled?.(screenIdx);
      }
    }
  }

  if (!tag) {
    return <p className="text-sm text-gray66">No API catalogue mapping for this step type.</p>;
  }

  return (
    <div>
      <TabHeader title="Method">
        Create a tailored, step-by-step automated workflow to manage and customize verification
        sessions.
      </TabHeader>

      {enabledCount === 0 && (
        <div className="flex items-center gap-2 px-3 py-2 mb-4 text-xs text-destructive bg-blue-tint border border-grayEF rounded-lg">
          At least one API must be enabled to activate this step.
        </div>
      )}

      {/* Table chrome ported from the designer's `.sc-method-table*`: #D0D4E4
          border, #F8FAFE header band, fluid cell padding and text-16 headings. */}
      <div className="w-full overflow-x-auto rounded-lg border border-[#D0D4E4]">
        <table className="w-full border-collapse">
          <thead className="border-b border-[#D0D4E4] bg-[#F8FAFE]">
            <tr>
              <th className="w-1/2 px-[clamp(12px,1vw,16px)] py-[clamp(6px,0.5vw,8px)] text-start text-[length:var(--text-16)] font-normal text-black-logo">
                API Name
              </th>
              <th className="px-[clamp(12px,1vw,16px)] py-[clamp(6px,0.5vw,8px)] text-start text-[length:var(--text-16)] font-normal text-black-logo">
                Status
              </th>
              <th className="px-[clamp(12px,1vw,16px)] py-[clamp(6px,0.5vw,8px)] text-start text-[length:var(--text-16)] font-normal text-black-logo">
                Priority
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading || !catalogue ? (
              [1, 2, 3].map((i) => (
                <tr key={i} className="border-b border-[#D0D4E4] last:border-0">
                  <td colSpan={3} className="h-11 animate-pulse bg-surface-soft" />
                </tr>
              ))
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleReorderBindings}
              >
                {/* Enabled APIs — drag to reorder priority (the fallback chain). */}
                <SortableContext
                  items={enabledRows.map((r) => r.entry.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {enabledRows.map((r) => (
                    <MethodRow
                      key={r.entry.id}
                      entry={r.entry}
                      priority={r.binding?.priority ?? 1}
                      isEnabled
                      draggable
                      disableToggle={enabledCount <= 1}
                      onToggle={(v) => setEntryEnabled(r.entry.id, v)}
                    />
                  ))}
                </SortableContext>
                {/* Disabled APIs — static, not part of the priority ordering. */}
                {disabledRows.map((r) => (
                  <MethodRow
                    key={r.entry.id}
                    entry={r.entry}
                    priority={null}
                    isEnabled={false}
                    draggable={false}
                    disableToggle={false}
                    onToggle={(v) => setEntryEnabled(r.entry.id, v)}
                  />
                ))}
              </DndContext>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// One row of the Method table. Enabled rows are drag-sortable (grip handle);
// disabled rows are static. Rendered as <tr> so the table stays valid HTML.
function MethodRow({
  entry,
  priority,
  isEnabled,
  draggable,
  disableToggle,
  onToggle,
}: {
  entry: { id: string; summary: string; docsUrl: string | null };
  priority: number | null;
  isEnabled: boolean;
  draggable: boolean;
  disableToggle: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.id,
    disabled: !draggable,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn('border-b border-[#D0D4E4] last:border-0', isDragging && 'bg-blue-tint')}
    >
      <td className="px-[clamp(12px,1vw,16px)] py-[clamp(8px,0.85vw,12px)]">
        <span className="flex items-center gap-1.5 text-[length:var(--text-14)] font-medium text-black-logo">
          {/* Grip handle on the left — only this picks up the row. */}
          {draggable && (
            <button
              type="button"
              aria-label="Drag to reorder"
              className="shrink-0 cursor-grab touch-none text-[#9CA3AF] transition-colors hover:text-[#374151] active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}
          <span className="truncate">{entry.summary}</span>
          {entry.docsUrl != null ? (
            <a
              href={entry.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-primary"
              aria-label="Open API docs"
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          ) : (
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          )}
        </span>
      </td>
      <td className="px-[clamp(12px,1vw,16px)] py-[clamp(8px,0.85vw,12px)]">
        <label className="relative flex w-fit cursor-pointer items-center">
          <input
            type="checkbox"
            className="peer sr-only"
            checked={isEnabled}
            disabled={isEnabled && disableToggle}
            onChange={(e) => onToggle(e.target.checked)}
          />
          <div className="h-4.5 w-8 rounded-full bg-grayEF transition-colors duration-200 peer-checked:bg-green00" />
          <div className="absolute start-0.5 top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-transform duration-200 peer-checked:translate-x-3.5" />
        </label>
      </td>
      <td className="px-[clamp(12px,1vw,16px)] py-[clamp(8px,0.85vw,12px)] text-[length:var(--text-14)] text-gray66">
        {priority ?? '—'}
      </td>
    </tr>
  );
}

// ── Settings Tab ─────────────────────────────────────────────────────────────

function SettingsTab({
  draft,
  onUpdate,
}: {
  draft: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
}) {
  const settings = parseSettings(draft.settings);

  function change<K extends keyof StepSettingsData>(key: K, value: StepSettingsData[K]) {
    onUpdate({ settings: JSON.stringify({ ...settings, [key]: value }) });
  }

  const sliders: {
    key: 'maxVerification' | 'lockDuration';
    title: string;
    desc: string;
    min: number;
    max: number;
    unit: 'ratio' | 'hrs' | 'min';
  }[] = [
    {
      key: 'maxVerification',
      title: 'Max Verification',
      desc: 'Maximum number of times a user can attempt verification before the session is locked.',
      min: 1,
      max: 10,
      unit: 'ratio',
    },
    {
      key: 'lockDuration',
      title: 'Lock Duration',
      desc: 'How long the session stays locked after exceeding max attempts.',
      min: 1,
      max: 48,
      unit: 'hrs',
    },
  ];

  const toggles: { key: 'autoLockOnExceed' | 'notifyOnFailure'; title: string; desc: string }[] = [
    {
      key: 'autoLockOnExceed',
      title: 'Auto-lock on Exceed',
      desc: 'Automatically lock the applicant session when max attempts are exceeded.',
    },
    {
      key: 'notifyOnFailure',
      title: 'Notify on Failure',
      desc: 'Email the tenant admin when a verification is rejected.',
    },
  ];

  return (
    <div>
      <TabHeader title="Settings">
        Create a tailored, step-by-step automated workflow to manage and customize verification
        sessions.
      </TabHeader>

      {/* Card grid ported from the designer's `.sc-settings-grid / __card`: 380px
          minimum track (was 280px, which squeezed every card), visible #B3B3B3
          border, roomier header gap and text-16 descriptions. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(380px,1fr))] gap-[clamp(12px,1vw,16px)]">
        {sliders.map((s) => {
          const value = settings[s.key];
          const progress = ((value - s.min) / (s.max - s.min)) * 100;
          return (
            <div
              key={s.key}
              className="flex flex-col rounded-md border border-placeholder bg-white p-[clamp(12px,1vw,12px)]"
            >
              <div className="mb-[clamp(12px,1.25vw,18px)]">
                <p className="m-0 mb-2 text-[length:var(--text-16)] font-semibold text-black-logo">
                  {s.title}
                </p>
                <p className="m-0 text-[length:var(--text-16)] leading-normal text-gray66">
                  {s.desc}
                </p>
              </div>
              <div className="mt-auto flex items-center gap-[clamp(12px,1vw,16px)] rounded-md px-[clamp(6px,0.5vw,8px)] py-[clamp(6px,0.5vw,8px)] ring-1 ring-[#E4E4E4]">
                <Timer className="h-3.5 w-3.5 shrink-0 text-gray30" />
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  value={value}
                  onChange={(e) => change(s.key, +e.target.value)}
                  className="sc-range"
                  style={{ '--progress': `${progress}%` } as CSSProperties}
                />
                <span className="text-placeholder">|</span>
                <span className="w-8 text-right text-[length:var(--text-12)] font-medium text-gray66">
                  <span className="text-[length:var(--text-14)] text-primary">
                    {s.unit === 'hrs' ? `${value}hrs` : s.unit === 'min' ? `${value}min` : value}
                  </span>
                  {s.unit === 'ratio' && ' /10'}
                </span>
              </div>
            </div>
          );
        })}

        {toggles.map((t) => (
          <div
            key={t.key}
            className="flex items-start justify-between gap-3 rounded-md border border-placeholder bg-white p-[clamp(12px,1vw,12px)]"
          >
            <div>
              <p className="m-0 mb-2 text-[length:var(--text-16)] font-semibold text-black-logo">
                {t.title}
              </p>
              <p className="m-0 text-[length:var(--text-16)] leading-normal text-gray66">
                {t.desc}
              </p>
            </div>
            <label className="relative mt-1 flex shrink-0 cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={settings[t.key]}
                onChange={(e) => change(t.key, e.target.checked)}
              />
              <div className="h-4.5 w-8 rounded-full bg-grayEF transition-colors duration-200 peer-checked:bg-green00" />
              <div className="absolute start-0.5 top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-transform duration-200 peer-checked:translate-x-3.5" />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

// A tab's heading + blurb. Sizes/colours ported from the designer's
// `.sc-config-tab__header / __title / __desc` (react-new-html
// WorkflowConfigPanel.css) — the previous text-sm/text-xs pair read far smaller
// than the design.
function TabHeader({
  title,
  children,
  action,
}: {
  title: string;
  children: string;
  // Optional control pinned to the right of the heading (Advanced Rules' "Add Rules").
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div>
        <p className="m-0 mb-1 text-[length:var(--text-18)] font-medium text-black-logo">{title}</p>
        <p className="m-0 max-w-lg text-[length:var(--text-16)] text-gray66">{children}</p>
      </div>
      {action}
    </div>
  );
}

// ── Risk Rules Tab ───────────────────────────────────────────────────────────

function RiskRulesTab({
  draft,
  onUpdate,
}: {
  draft: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
}) {
  const rules = draft.riskRules;

  function updateRule(idx: number, patch: Partial<StepRiskRuleDto>) {
    onUpdate({
      riskRules: rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    });
  }

  const header = (
    <TabHeader title="Risk Rules">
      Create a tailored, step-by-step automated workflow to manage and customize verification
      sessions.
    </TabHeader>
  );

  if (rules.length === 0) {
    return (
      <div>
        {header}
        <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-grayEF rounded-xl">
          <p className="text-sm font-medium text-black-logo">No risk rules</p>
          <p className="text-xs text-gray66 mt-1">Risk rules are applied after the API response.</p>
        </div>
      </div>
    );
  }

  // Each rule tagged with its (optional) document scope + clean key. For the ID
  // Verification step, rules are "<doc>::<key>"; every other step is un-prefixed
  // (doc=null) and renders as a single flat list exactly as before.
  const indexed = rules.map((rule, idx) => {
    const { doc, key } = splitDocScope(rule.ruleKey);
    return { rule, idx, doc, cleanKey: key };
  });
  const perDocument = indexed.some((r) => r.doc !== null);

  const renderRule = ({
    rule,
    idx,
    cleanKey,
  }: {
    rule: StepRiskRuleDto;
    idx: number;
    cleanKey: string;
  }) => {
    const meta = ID_RISK_RULE_META[cleanKey] ??
      RISK_RULE_META[cleanKey] ?? { label: cleanKey, description: '' };
    const cfg = RULE_CONFIG_META[cleanKey];
    return (
      <div
        key={rule.id}
        className="flex flex-col gap-1 rounded-lg border border-grayEF bg-white p-3"
      >
        <h4 className="w-full text-[length:var(--text-16)] font-semibold text-black-logo">
          {meta.label}
        </h4>
        <div className="flex w-full flex-wrap justify-between gap-2">
          <div className="flex-1 2xl:max-w-[320px]">
            {meta.description && (
              <p className="text-[length:var(--text-14)] text-gray66">{meta.description}</p>
            )}
            {cfg && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <label htmlFor={`cfg-${rule.id}`} className="text-xs text-gray66">
                  {cfg.label}
                </label>
                <input
                  id={`cfg-${rule.id}`}
                  type="number"
                  min={cfg.min}
                  max={cfg.max}
                  value={rule.config ?? ''}
                  placeholder={String(cfg.placeholder)}
                  onChange={(e) => updateRule(idx, { config: e.target.value || null })}
                  className="w-14 rounded-md border border-grayEF bg-white px-2 py-0.5 text-xs text-black-logo transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                />
                <span className="text-xs text-gray66">{cfg.suffix}</span>
              </div>
            )}
          </div>
          <div className="flex flex-1 items-center justify-start gap-3 sm:gap-4 2xl:justify-end">
            {(['Approve', 'Review', 'Reject'] as const).map((action) => {
              const isActive = rule.action === action;
              let activeCls = 'border-[#00A92C] bg-[#E8F5E9] text-[#00A92C]';
              if (action === 'Review') activeCls = 'border-primary bg-[#E5EDFF] text-primary';
              else if (action === 'Reject')
                activeCls = 'border-[#EF4444] bg-[#FEE2E2] text-[#EF4444]';

              return (
                <button
                  key={action}
                  type="button"
                  onClick={() => updateRule(idx, { action })}
                  className={cn(
                    'flex cursor-pointer items-center gap-1.5 rounded-full border px-[clamp(10px,0.8vw,12px)] py-[clamp(3px,0.25vw,4px)] text-[length:var(--text-14)] tracking-tight transition-all',
                    isActive
                      ? cn(activeCls, 'font-medium')
                      : 'border-[#A5A5A5] bg-grayEF text-gray66',
                  )}
                >
                  <span
                    className={cn(
                      'h-2.5 w-2.5 shrink-0 rounded-full border-[1.5px] border-current',
                      isActive && 'bg-current',
                    )}
                  />
                  {action}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (!perDocument) {
    return (
      <div>
        {header}
        <div className="flex flex-col gap-3">{indexed.map(renderRule)}</div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <div className="flex flex-col gap-5">
        {ID_DOC_ORDER.map((doc) => {
          const docRules = indexed.filter((r) => r.doc === doc);
          if (docRules.length === 0) return null;
          return (
            <div key={doc} className="flex flex-col gap-3">
              <h3 className="text-[length:var(--text-13)] font-semibold uppercase tracking-wide text-primary">
                {ID_DOC_LABELS[doc] ?? doc}
              </h3>
              {docRules.map(renderRule)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Advanced Rules Tab ───────────────────────────────────────────────────────
// Rule groups: each "Rule N" card can hold multiple conditions connected by AND/OR.
// Groups are tracked in local state — flattened to the flat `conditions` array on save.

type RuleGroup = {
  groupId: string;
  conditionIds: string[];
  connector: 'AND' | 'OR';
};

// Reconstruct rule groups from the grouping persisted in the step's settings
// JSON, reconciled against the step's current conditions: stale ids are
// dropped, and any condition not already in a group is added as its own group.
// Falls back to one-group-per-condition when nothing was saved.
function buildRuleGroups(conditions: { id: string }[], settings: string | null): RuleGroup[] {
  const saved = parseSettingsObj(settings).__ruleGroups as RuleGroup[] | undefined;
  const ids = new Set(conditions.map((c) => c.id));
  if (Array.isArray(saved) && saved.length > 0) {
    const cleaned = saved
      .map((g) => ({ ...g, conditionIds: g.conditionIds.filter((id) => ids.has(id)) }))
      .filter((g) => g.conditionIds.length > 0);
    const tracked = new Set(cleaned.flatMap((g) => g.conditionIds));
    const extra = conditions
      .filter((c) => !tracked.has(c.id))
      .map((c) => ({ groupId: c.id, conditionIds: [c.id], connector: 'AND' as const }));
    return [...cleaned, ...extra];
  }
  return conditions.map((c) => ({
    groupId: c.id,
    conditionIds: [c.id],
    connector: 'AND' as const,
  }));
}

// Serialize the grouping back into the settings JSON, leaving all other
// settings keys (maxVerification, lockDuration, …) untouched.
function serializeWithGroups(settings: string | null, groups: RuleGroup[]): string {
  return JSON.stringify({ ...parseSettingsObj(settings), __ruleGroups: groups });
}

// Fallback field options for the rule "Field" dropdown when a step has no
// resolved return fields yet (return fields only populate after API bindings
// are saved + executed). Keeps the Advanced Rules dropdown usable meanwhile.
const DEFAULT_CONDITION_FIELDS: { value: string; label: string }[] = [
  { value: 'pan_status', label: 'PAN Status' },
  { value: 'pan_aadhaar_link', label: 'PAN-Aadhaar Link' },
  { value: 'name_match_score', label: 'Name Match Score' },
  { value: 'pan_category', label: 'PAN Category' },
  { value: 'age', label: 'Age' },
];

// Then-action palette, ported from the designer's ACTION_STYLES. Note the pill
// renders on WHITE: their styles object defines a tint but never applies it to
// the trigger, so the rendered design is a coloured outline only.
type ActionType = 'Approve' | 'Review' | 'Reject';

const ACTION_TYPES: readonly ActionType[] = ['Approve', 'Review', 'Reject'];

const ACTION_STYLES: Record<ActionType, { border: string; text: string; dot: string }> = {
  Approve: { border: 'border-[#22C55E]', text: 'text-[#22C55E]', dot: 'bg-[#22C55E]' },
  Review: { border: 'border-[#0B5CFF]', text: 'text-[#0B5CFF]', dot: 'bg-[#0B5CFF]' },
  Reject: { border: 'border-[#EF4444]', text: 'text-[#EF4444]', dot: 'bg-[#EF4444]' },
};

// thenAction is a free-form string on the DTO; anything unrecognised renders as
// Approve rather than crashing on an unknown key.
function asActionType(value: string): ActionType {
  return ACTION_TYPES.includes(value as ActionType) ? (value as ActionType) : 'Approve';
}

// Shared control metrics from `.sc-adv-rule-select` / `.sc-adv-rule-input`.
const ADV_CONTROL =
  'border border-placeholder rounded-lg px-[clamp(6px,0.5vw,8px)] py-[clamp(6px,0.5vw,6px)] text-[length:var(--text-14)] text-gray30 outline-none focus:ring-0';

function ConditionRow({
  condition,
  fieldOptions,
  canDelete,
  onUpdate,
  onDelete,
}: {
  condition: StepConditionDto;
  fieldOptions: { value: string; label: string }[];
  canDelete: boolean;
  onUpdate: (patch: Partial<StepConditionDto>) => void;
  onDelete: () => void;
}) {
  const action = ACTION_STYLES[asActionType(condition.thenAction)];

  return (
    <div className="flex flex-wrap items-center justify-evenly gap-[clamp(6px,0.5vw,8px)] 2xl:justify-between">
      <select
        className={cn(
          'sc-select w-[clamp(88px,9vw,104px)] shrink-0 bg-[right_4px_center] pe-5',
          ADV_CONTROL,
        )}
        value={condition.ifField}
        onChange={(e) => onUpdate({ ifField: e.target.value })}
      >
        <option value="">Field</option>
        {fieldOptions.map((f) => (
          <option key={f.value} value={f.value}>
            {f.label}
          </option>
        ))}
      </select>

      <select
        className={cn(
          'sc-select w-[clamp(88px,9vw,104px)] shrink-0 bg-[right_4px_center] pe-5',
          ADV_CONTROL,
        )}
        value={condition.operator}
        onChange={(e) => onUpdate({ operator: e.target.value })}
      >
        <option value="equals">Equals</option>
        <option value="not_equals">Not Equals</option>
        <option value="contains">Contains</option>
        <option value="greater_than">Greater Than</option>
        <option value="less_than">Less Than</option>
      </select>

      {/* `normal-case` overrides the global capitalising placeholder rule — the
          design's placeholder reads "Type a value", not "Type A Value". */}
      <input
        type="text"
        className={cn(
          'sc-input w-[clamp(140px,15vw,177px)] shrink-0 placeholder:normal-case',
          ADV_CONTROL,
        )}
        placeholder="Type a value"
        value={condition.ifValue}
        onChange={(e) => onUpdate({ ifValue: e.target.value })}
      />

      <span className="shrink-0 text-[#9CA3AF]">→</span>

      {/* Coloured outline pill with a status dot — a native <select> can't render
          the dot, so this is a menu over a button (same value, same onUpdate). */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex min-w-[clamp(80px,6vw,100px)] shrink-0 cursor-pointer items-center justify-between gap-1 whitespace-nowrap rounded-lg border bg-white px-[clamp(6px,0.5vw,8px)] py-[clamp(6px,0.5vw,6px)] text-[length:var(--text-14)] font-medium transition-colors',
              action.border,
              action.text,
            )}
          >
            <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', action.dot)} />
            {condition.thenAction}
            <ChevronDown className="h-3 w-3 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[110px] p-1">
          {ACTION_TYPES.map((type) => (
            <DropdownMenuItem
              key={type}
              onClick={() => onUpdate({ thenAction: type })}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-[12px] font-medium',
                ACTION_STYLES[type].text,
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', ACTION_STYLES[type].dot)} />
              {type}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="shrink-0 p-1 text-[#9CA3AF] transition-colors hover:text-red-500"
          title="Remove condition"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function AdvancedRulesTab({
  draft,
  onUpdate,
}: {
  draft: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
}) {
  // Grouping + AND/OR persists in the step's settings JSON (round-trips on save).
  const [groups, setGroups] = useState<RuleGroup[]>(() =>
    buildRuleGroups(draft.conditions, draft.settings),
  );

  // Rebuild groups when the underlying conditions change identity — e.g.
  // switching to another step, or a server refetch after save. Reads the saved
  // grouping from that step's settings so the layout is restored, not flattened.
  const prevCondIds = useRef(draft.conditions.map((c) => c.id).join(','));
  useEffect(() => {
    const current = draft.conditions.map((c) => c.id).join(',');
    if (current === prevCondIds.current) return;
    prevCondIds.current = current;
    setGroups(buildRuleGroups(draft.conditions, draft.settings));
  }, [draft.conditions, draft.settings]);

  const makeCondition = useCallback(
    (): StepConditionDto => ({
      id: crypto.randomUUID(),
      sortOrder: draft.conditions.length,
      ifField: '',
      operator: 'equals',
      ifValue: '',
      thenAction: 'Approve',
    }),
    [draft.conditions.length],
  );

  // Mutations update conditions + groups together and persist the grouping into
  // settings in the SAME onUpdate, so a single change marks one dirty edit and
  // the layout survives reload.
  function addNewRule() {
    const cond = makeCondition();
    const nextGroups: RuleGroup[] = [
      ...groups,
      { groupId: cond.id, conditionIds: [cond.id], connector: 'AND' },
    ];
    setGroups(nextGroups);
    onUpdate({
      conditions: [...draft.conditions, cond],
      settings: serializeWithGroups(draft.settings, nextGroups),
    });
  }

  function addConditionToGroup(groupId: string) {
    const cond = makeCondition();
    const nextGroups = groups.map((g) =>
      g.groupId === groupId ? { ...g, conditionIds: [...g.conditionIds, cond.id] } : g,
    );
    setGroups(nextGroups);
    onUpdate({
      conditions: [...draft.conditions, cond],
      settings: serializeWithGroups(draft.settings, nextGroups),
    });
  }

  function removeCondition(condId: string) {
    const nextGroups = groups
      .map((g) => ({ ...g, conditionIds: g.conditionIds.filter((id) => id !== condId) }))
      .filter((g) => g.conditionIds.length > 0);
    setGroups(nextGroups);
    onUpdate({
      conditions: draft.conditions.filter((c) => c.id !== condId),
      settings: serializeWithGroups(draft.settings, nextGroups),
    });
  }

  function updateCondition(condId: string, patch: Partial<StepConditionDto>) {
    onUpdate({
      conditions: draft.conditions.map((c) => (c.id === condId ? { ...c, ...patch } : c)),
    });
  }

  function toggleConnector(groupId: string) {
    const nextGroups = groups.map((g) =>
      g.groupId === groupId
        ? { ...g, connector: g.connector === 'AND' ? ('OR' as const) : ('AND' as const) }
        : g,
    );
    setGroups(nextGroups);
    onUpdate({ settings: serializeWithGroups(draft.settings, nextGroups) });
  }

  const condMap = useMemo(
    () => new Map(draft.conditions.map((c) => [c.id, c])),
    [draft.conditions],
  );

  // Use the step's resolved return fields when available; otherwise fall back
  // to a sensible default set so the "Field" dropdown is never empty. For the
  // combined ID Verification step, fields are namespaced "<doc>::<key>", so the
  // dropdown label is tagged with the document ("Voter ID · EPIC Number") — the
  // condition's stored ifField keeps the prefix, so the engine automatically
  // scopes that advanced rule to the chosen document (per-method advanced rules).
  const fieldOptions = useMemo(
    () =>
      draft.returnFields.length > 0
        ? draft.returnFields.map((f) => {
            const { doc } = splitDocScope(f.fieldKey);
            const label = doc ? `${ID_DOC_LABELS[doc] ?? doc} · ${f.displayLabel}` : f.displayLabel;
            return { value: f.fieldKey, label };
          })
        : DEFAULT_CONDITION_FIELDS,
    [draft.returnFields],
  );

  return (
    <div>
      <TabHeader
        title="Advanced Rules"
        action={
          <button
            type="button"
            onClick={addNewRule}
            className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border-none bg-primary px-[clamp(8px,0.6vw,12px)] py-[clamp(3px,0.25vw,4px)] text-[length:var(--text-16)] font-semibold text-white transition-colors hover:bg-[#0a52e0]"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Rules
          </button>
        }
      >
        Set up custom rules that automatically determine feature status based on defined conditions.
      </TabHeader>

      {/* Rule group cards */}
      {groups.map((group, groupIdx) => {
        const conditions = group.conditionIds
          .map((id) => condMap.get(id))
          .filter((c): c is StepConditionDto => c != null);

        return (
          <div
            key={group.groupId}
            className="mb-[clamp(12px,1.1vw,16px)] overflow-hidden rounded-lg border border-[#CBD5E1] bg-white last:mb-0"
          >
            {/* Rule header — `.sc-adv-rule-card__header`: #F8FAFE band, 16px
                regular title, pill + button. */}
            <div className="flex items-center justify-between border-b border-[#CBD5E1] bg-[#F8FAFE] px-[clamp(12px,0.9vw,14px)] py-[clamp(4px,0.4vw,6px)]">
              <h4 className="m-0 text-[length:var(--text-16)] font-normal text-black-logo">
                Rule {groupIdx + 1}
              </h4>
              <button
                type="button"
                onClick={() => addConditionToGroup(group.groupId)}
                title="Add condition to this rule"
                className="flex h-5 w-5 items-center justify-center rounded-full bg-[#E5EDFF] text-[#0048FF] transition-colors hover:bg-[#D5E1FF]"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>

            {/* Conditions. The AND/OR control floats on the row's bottom border
                (`.sc-adv-rule-row__or`) rather than sitting in its own full-width
                row — that's the design's divider-straddling pill. */}
            <div className="p-[clamp(6px,0.5vw,8px)]">
              {conditions.map((condition, condIdx) => {
                const isLast = condIdx === conditions.length - 1;
                return (
                  <div
                    key={condition.id}
                    className={cn(
                      'relative p-[clamp(6px,0.5vw,8px)]',
                      !isLast &&
                        'mb-[clamp(12px,1vw,16px)] border-b border-[#E5E7EB] pb-[clamp(10px,1vw,16px)]',
                    )}
                  >
                    <ConditionRow
                      condition={condition}
                      fieldOptions={fieldOptions}
                      canDelete={true}
                      onUpdate={(patch) => updateCondition(condition.id, patch)}
                      onDelete={() => removeCondition(condition.id)}
                    />

                    {!isLast && (
                      <button
                        type="button"
                        onClick={() => toggleConnector(group.groupId)}
                        title="Toggle AND / OR"
                        className="absolute -bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 bg-white px-2 text-[10px] font-bold text-[#9CA3AF]"
                      >
                        OR
                        <span
                          className={cn(
                            'relative h-3.5 w-6 rounded-full transition-colors duration-200',
                            group.connector === 'AND' ? 'bg-green00' : 'bg-grayEF',
                          )}
                        >
                          <span
                            className={cn(
                              'absolute top-1/2 left-[2px] h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-white shadow transition-transform duration-200',
                              group.connector === 'AND' && 'translate-x-[10px]',
                            )}
                          />
                        </span>
                        AND
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Empty state */}
      {groups.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-grayEF rounded-xl">
          <p className="text-sm font-medium text-black-logo">No rules yet</p>
          <p className="text-xs text-gray66 mt-1 max-w-xs">
            Click "+ Add Rules" to define conditions that determine the verification outcome.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Return Data Tab ──────────────────────────────────────────────────────────

// Fixed display order so toggling a field NEVER restructures the layout. Groups
// render as columns in this order; fields sort within their group by this key
// order. The persisted returnFields array has no sortOrder, so a save+refetch can
// return them in a different order — without this, the columns/fields would jump
// around ("criss-cross") on every toggle. Keys/groups not listed here fall to the
// end (stable, alphabetical by label). Covers both PAN and Aadhaar return fields.
const RETURN_GROUP_ORDER = [
  'Identity Details',
  'Personal Information',
  'Business Information',
  'Bank Details',
  'Card Details',
  'Household Details',
  // MSME (Udyam)
  'Identity & Classification',
  'Enterprise Profile',
  'Compliance & Contact',
  'Employment & Financial',
  'Address & Location',
  'Document',
  'Address & Jurisdiction Details',
  'Verification Results',
  'Risk Signals',
  'Metadata',
  // ID Verification — combined "<Document> · <Section>" groups, so the builder
  // renders one block per document (Voter ID → Passport → Driving Licence).
  'Voter ID · Identity Details',
  'Voter ID · Personal Information',
  'Voter ID · Address & Constituency Details',
  'Voter ID · Risk Signals',
  'Voter ID · Metadata',
  'Passport · Identity Details',
  'Passport · Personal Information',
  'Passport · Issue Details',
  'Passport · Risk Signals',
  'Passport · Metadata',
  'Driving Licence · Identity Details',
  'Driving Licence · Personal Information',
  'Driving Licence · License & Coverage Details',
  'Driving Licence · Risk Signals',
  'Driving Licence · Endorsement & Hazardous Details',
  'Driving Licence · Metadata',
  // Utility Bill — combined "<Type> · <Section>" groups (Electricity → PNG → LPG),
  // section names taken verbatim from the Studio PDF Return-Data tables.
  'Electricity · Identity & Bill Details',
  'Electricity · Customer Information',
  'Electricity · Financial Details',
  'Electricity · Risk Signals',
  'Electricity · Metadata',
  'PNG · Identity & Bill Details',
  'PNG · Customer Information',
  'PNG · Financial Details',
  'PNG · Risk Signals',
  'PNG · Metadata',
  'LPG · Identity Details',
  'LPG · Customer Information',
  'LPG · Subsidy & Banking Details',
  'LPG · Consumption & Activity Details',
  'LPG · Risk Signals',
  'LPG · Metadata',
] as const;

const RETURN_FIELD_ORDER = [
  // PAN
  'pan_number',
  'pan_status',
  'pan_holder_name',
  'date_of_birth',
  'pan_category',
  'age',
  'name_match_score',
  'duplicate_pan_flag',
  'verification_status',
  'verified_at',
  'method_used',
  'verification_attempts',
  'session_id',
  // Aadhaar (keys not already listed above)
  'reference_id',
  'name',
  'dob',
  'gender',
  'address',
  'pincode',
  'state',
  'masked_mobile',
  'masked_email',
  'consent_captured_at',
  // Face Match (real FaceMatch/verify response)
  'face_matched',
  'face_match_score',
  'confidence',
  'brightness',
  'sharpness',
  'review_needed',
  'capture_attempts',
  // Liveness (real FaceLiveness response; keys not already listed above)
  'liveness_passed',
  'liveness_score',
  'multiple_faces_detected',
  // Bank Account Verification (keys not already listed above)
  'account_number',
  'account_holder_name',
  'bank_name',
  'ifsc',
  'account_status',
  'bank_reference',
  'name_matched_against',
  // GST Verification (keys not already listed above)
  'gst_identification_number',
  'gstin_status',
  'legal_name_of_business',
  'trade_name',
  'constitution_of_business',
  'taxpayer_type',
  'date_of_registration',
  'principal_place_of_business_address',
  'state_jurisdiction',
  'center_jurisdiction',
  'duplicate_gstin_flag',
  // Ration Card Verification (keys not already listed above)
  'ration_card_number',
  'district',
  'fps_id',
  'scheme_name',
  'onorc_allowed',
  'head_of_family',
  'member_count',
  'members',
  'duplicate_uid_status',
  'duplicate_ration_card_flag',
  // MSME (Udyam) Verification (keys not already listed above)
  'udyam_registration_no',
  'type_of_enterprise',
  'enterprise_type_history',
  'industry_classification',
  'interested_in_treds',
  'interested_in_gem',
  'business_name',
  'major_activity',
  'organization_type',
  'owner_name',
  'pan',
  'social_category',
  'specially_abled',
  'date_of_incorporation',
  'date_of_commencement',
  'previous_itr_status',
  'itr_type',
  'gstn_status',
  'mobile',
  'email_id',
  'ifsc_code',
  'bank_account_no',
  'employment_male',
  'employment_female',
  'employment_others',
  'employment_total',
  'financial_year',
  'wdv_pme',
  'exclusion',
  'net_investment_in_pme',
  'total_turnover',
  'export_turnover',
  'net_turnover',
  'official_address',
  'branch_details',
  'pdf_document',
  'enterprise_type_mismatch_flag',
  'pan_match_flag',
  'turnover_threshold_flag',
  'duplicate_udyam_flag',
] as const;

function orderIndex(list: readonly string[], value: string): number {
  const i = list.indexOf(value);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

function ReturnDataTab({
  draft,
  onUpdate,
  guards,
}: {
  draft: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
  /** fieldKey → reason: fields that can't be toggled (unmet cross-step dependency). */
  guards?: Record<string, string>;
}) {
  const fields = useMemo(() => {
    if (draft.stepType === 'pan_verification' && draft.returnFields.length === 0) {
      return [
        {
          id: crypto.randomUUID(),
          fieldKey: 'pan_number',
          fieldGroup: 'Identity Details',
          displayLabel: 'PAN Number',
          isIncluded: true,
          isDefaultIncluded: true,
        },
        {
          id: crypto.randomUUID(),
          fieldKey: 'pan_status',
          fieldGroup: 'Identity Details',
          displayLabel: 'PAN Status',
          isIncluded: true,
          isDefaultIncluded: true,
        },
        {
          id: crypto.randomUUID(),
          fieldKey: 'pan_holder_name',
          fieldGroup: 'Personal Information',
          displayLabel: 'PAN Holder Name',
          isIncluded: true,
          isDefaultIncluded: true,
        },
        {
          id: crypto.randomUUID(),
          fieldKey: 'date_of_birth',
          fieldGroup: 'Personal Information',
          displayLabel: 'Date of Birth',
          isIncluded: true,
          isDefaultIncluded: true,
        },
        {
          id: crypto.randomUUID(),
          fieldKey: 'pan_category',
          fieldGroup: 'Personal Information',
          displayLabel: 'PAN Category',
          isIncluded: false,
          isDefaultIncluded: false,
        },
        {
          id: crypto.randomUUID(),
          fieldKey: 'age',
          fieldGroup: 'Personal Information',
          displayLabel: 'Age',
          isIncluded: false,
          isDefaultIncluded: false,
        },
      ];
    }
    return draft.returnFields;
  }, [draft.stepType, draft.returnFields]);

  // Group by fieldGroup, then order the groups (columns) and the fields within
  // each group deterministically (RETURN_GROUP_ORDER / RETURN_FIELD_ORDER) so the
  // layout is stable — enabling/disabling a toggle can never move anything.
  const groups = useMemo(() => {
    const byGroup = new Map<string, StepReturnFieldDto[]>();
    for (const field of fields) {
      const groupName = field.fieldGroup || 'General';
      const list = byGroup.get(groupName);
      if (list) list.push(field);
      else byGroup.set(groupName, [field]);
    }
    return [...byGroup.entries()]
      .map(([groupName, items]) => ({
        groupName,
        items: [...items].sort(
          (a, b) =>
            orderIndex(RETURN_FIELD_ORDER, a.fieldKey) -
              orderIndex(RETURN_FIELD_ORDER, b.fieldKey) ||
            a.displayLabel.localeCompare(b.displayLabel),
        ),
      }))
      .sort(
        (a, b) =>
          orderIndex(RETURN_GROUP_ORDER, a.groupName) -
            orderIndex(RETURN_GROUP_ORDER, b.groupName) || a.groupName.localeCompare(b.groupName),
      );
  }, [fields]);

  // A guarded field can't be enabled (unmet cross-step dependency) — it stays OFF.
  const isGuarded = (fieldKey: string) => Boolean(guards?.[fieldKey]);

  function toggleGroup(groupName: string, isIncluded: boolean) {
    onUpdate({
      returnFields: fields.map((f) =>
        (f.fieldGroup === groupName || (groupName === 'General' && !f.fieldGroup)) &&
        !isGuarded(f.fieldKey)
          ? { ...f, isIncluded }
          : f,
      ),
    });
  }

  // Keyed on the field's stable id (not array index) so it stays correct
  // regardless of the persisted order. No-ops for guarded fields.
  function toggleField(id: string, isIncluded: boolean) {
    onUpdate({
      returnFields: fields.map((f) =>
        f.id === id && !isGuarded(f.fieldKey) ? { ...f, isIncluded } : f,
      ),
    });
  }

  return (
    <div className="space-y-3">
      <TabHeader title="Return Data">
        Select which fields returned by this step's APIs are included in the verification result.
        Enabled fields are available to downstream rules and system callbacks.
      </TabHeader>

      {fields.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-grayEF rounded-xl">
          <p className="text-sm font-medium text-black-logo">No return fields yet</p>
          <p className="text-xs text-gray66 mt-1 max-w-xs">
            Return fields are populated automatically once the step's API bindings are saved and
            executed.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-x-6 gap-y-6 xl:grid-cols-3">
          {groups.map(({ groupName, items }) => {
            const anyIncluded = items.some(
              (field) => field.isIncluded && !isGuarded(field.fieldKey),
            );
            return (
              <div key={groupName}>
                {/* Group header — toggle + name */}
                <label className="flex cursor-pointer items-center gap-2.5">
                  <span className="relative flex shrink-0 items-center">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={anyIncluded}
                      onChange={(e) => toggleGroup(groupName, e.target.checked)}
                    />
                    <span className="block h-4.5 w-8 rounded-full bg-grayEF transition-colors duration-200 peer-checked:bg-green00" />
                    <span className="absolute start-0.5 top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-transform duration-200 peer-checked:translate-x-3.5" />
                  </span>
                  <span className="text-sm font-semibold text-black-logo">{groupName}</span>
                </label>

                {/* Fields */}
                <div className="mt-3 space-y-2.5 ps-[42px]">
                  {items.map((field) => {
                    const guardReason = guards?.[field.fieldKey];
                    const guarded = Boolean(guardReason);
                    return (
                      <div key={field.id}>
                        <label
                          className={cn(
                            'flex items-center gap-2.5',
                            guarded ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                          )}
                        >
                          <span className="relative flex shrink-0 items-center">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={field.isIncluded && !guarded}
                              disabled={guarded}
                              onChange={(e) => toggleField(field.id, e.target.checked)}
                            />
                            <span className="block h-4.5 w-8 rounded-full bg-grayEF transition-colors duration-200 peer-checked:bg-green00" />
                            <span className="absolute start-0.5 top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-transform duration-200 peer-checked:translate-x-3.5" />
                          </span>
                          <span className="text-sm text-gray30">{field.displayLabel}</span>
                        </label>
                        {guarded && (
                          <p className="ps-[42px] pt-1 text-[11px] leading-snug text-[#BF3636]">
                            {guardReason}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
